/**
 * Main Application Component
 *
 * Sets up routing, global providers, and error boundaries.
 *
 * @module App
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/components/ui';
import { ProtectedRoute, RoleProtectedRoute } from '@/components/auth';
import { DashboardLayout, SettingsLayout, SettingsIndexRedirect } from '@/components/layout';
import { PageErrorBoundary } from '@/components/ErrorBoundary';

// Auth Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';

// Onboarding
import { OnboardingPage } from '@/pages/OnboardingPage';
import { InvitedOnboardingPage } from '@/pages/InvitedOnboardingPage';

// Dashboard Pages
import { DashboardPage } from '@/pages/DashboardPage';
import { KeysPage } from '@/pages/KeysPage';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';

// Settings Pages
import { OrganizationPage } from '@/pages/settings/OrganizationPage';
import { ProfilePage } from '@/pages/settings/ProfilePage';
import { TeamPage } from '@/pages/settings/TeamPage';
import { BillingPage } from '@/pages/settings/BillingPage';
import { BillingSuccessPage } from '@/pages/settings/BillingSuccessPage';

// Admin Pages
import { AdminPage } from '@/pages/admin';

// =============================================================================
// App Component
// =============================================================================

export default function App() {
  return (
    <PageErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />

            {/* Auth Flow Routes (require auth but not onboarding) */}
            <Route
              path="/verify-email"
              element={
                <ProtectedRoute requireEmailVerified={false} requireOnboarding={false}>
                  <VerifyEmailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireEmailVerified={true} requireOnboarding={false}>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invited-onboarding"
              element={
                <ProtectedRoute requireEmailVerified={true} requireOnboarding={false}>
                  <InvitedOnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Main Pages */}
              <Route path="/overview" element={<DashboardPage />} />
              <Route path="/keys" element={<KeysPage />} />
              <Route path="/profile" element={<ProfilePage />} />

              {/* Admin Pages (Super Admin Only) */}
              <Route path="/admin" element={<AdminPage />} />

              {/* Settings Pages */}
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<SettingsIndexRedirect />} />
                <Route
                  path="organization"
                  element={
                    <RoleProtectedRoute
                      requiredPermission={(p) => p.canUpdateOrgSettings}
                      redirectTo="/settings/team"
                    >
                      <OrganizationPage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="team"
                  element={
                    <RoleProtectedRoute
                      requiredPermission={(p) => p.canManageTeam}
                      redirectTo="/settings/billing"
                    >
                      <TeamPage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="billing"
                  element={
                    <RoleProtectedRoute
                      requiredPermission={(p) => p.canAccessBilling}
                      redirectTo="/profile"
                    >
                      <BillingPage />
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path="billing/success"
                  element={
                    <RoleProtectedRoute
                      requiredPermission={(p) => p.canAccessBilling}
                      redirectTo="/profile"
                    >
                      <BillingSuccessPage />
                    </RoleProtectedRoute>
                  }
                />
              </Route>

              {/* Redirect old /settings/profile to new /profile */}
              <Route path="/settings/profile" element={<Navigate to="/profile" replace />} />
            </Route>

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </PageErrorBoundary>
  );
}
